import { ref, watch } from 'vue';

// Storage keys
const STORAGE_KEYS = {
  CHAT_INTERACTION: 'ie_chat_interaction',
  CHAT_PREFERENCES: 'ie_chat_preferences',
  CHAT_DISMISSED_WELCOME: 'ie_chat_dismissed_welcome',
  CHAT_USER_INFO: 'ie_chat_user_info'
};

// Reactive storage state
const hasInteracted = ref(false);
const userPreferences = ref({
  showWelcomeMessage: true,
  autoShowDelay: 15000,
  soundEnabled: true,
  notificationsEnabled: true
});
const userInfo = ref(null);

/**
 * Safe localStorage operations with error handling
 */
class SafeStorage {
  static isAvailable() {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  static getItem(key, defaultValue = null) {
    if (!this.isAvailable()) {
      return defaultValue;
    }

    try {
      const item = localStorage.getItem(key);
      if (item === null) {
        return defaultValue;
      }
      return JSON.parse(item);
    } catch (error) {
      console.warn(`Failed to parse localStorage item ${key}:`, error);
      return defaultValue;
    }
  }

  static setItem(key, value) {
    if (!this.isAvailable()) {
      return false;
    }

    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.warn(`Failed to set localStorage item ${key}:`, error);
      return false;
    }
  }

  static removeItem(key) {
    if (!this.isAvailable()) {
      return false;
    }

    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.warn(`Failed to remove localStorage item ${key}:`, error);
      return false;
    }
  }

  static clear() {
    if (!this.isAvailable()) {
      return false;
    }

    try {
      // Only clear our specific keys
      Object.values(STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
      return true;
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
      return false;
    }
  }
}

export function useLocalStorage() {
  /**
   * Initialize storage state from localStorage
   */
  const initializeStorage = () => {
    // Load interaction state
    const savedInteraction = SafeStorage.getItem(STORAGE_KEYS.CHAT_INTERACTION, false);
    hasInteracted.value = Boolean(savedInteraction);

    // Load user preferences
    const savedPreferences = SafeStorage.getItem(STORAGE_KEYS.CHAT_PREFERENCES, {});
    userPreferences.value = {
      ...userPreferences.value,
      ...savedPreferences
    };

    // Load user info
    const savedUserInfo = SafeStorage.getItem(STORAGE_KEYS.CHAT_USER_INFO, null);
    userInfo.value = savedUserInfo;
  };

  /**
   * Mark that user has interacted with chat
   */
  const markAsInteracted = () => {
    hasInteracted.value = true;
    SafeStorage.setItem(STORAGE_KEYS.CHAT_INTERACTION, true);
  };

  /**
   * Reset interaction state
   */
  const resetInteraction = () => {
    hasInteracted.value = false;
    SafeStorage.removeItem(STORAGE_KEYS.CHAT_INTERACTION);
  };

  /**
   * Update user preferences
   */
  const updatePreferences = (newPreferences) => {
    userPreferences.value = {
      ...userPreferences.value,
      ...newPreferences
    };
    SafeStorage.setItem(STORAGE_KEYS.CHAT_PREFERENCES, userPreferences.value);
  };

  /**
   * Get a specific preference
   */
  const getPreference = (key, defaultValue = null) => {
    return userPreferences.value[key] ?? defaultValue;
  };

  /**
   * Set a specific preference
   */
  const setPreference = (key, value) => {
    userPreferences.value[key] = value;
    SafeStorage.setItem(STORAGE_KEYS.CHAT_PREFERENCES, userPreferences.value);
  };

  /**
   * Save user information
   */
  const saveUserInfo = (info) => {
    userInfo.value = info;
    SafeStorage.setItem(STORAGE_KEYS.CHAT_USER_INFO, info);
  };

  /**
   * Clear user information
   */
  const clearUserInfo = () => {
    userInfo.value = null;
    SafeStorage.removeItem(STORAGE_KEYS.CHAT_USER_INFO);
  };

  /**
   * Check if welcome message was dismissed
   */
  const isWelcomeDismissed = () => {
    return SafeStorage.getItem(STORAGE_KEYS.CHAT_DISMISSED_WELCOME, false);
  };

  /**
   * Mark welcome message as dismissed
   */
  const dismissWelcomeMessage = () => {
    SafeStorage.setItem(STORAGE_KEYS.CHAT_DISMISSED_WELCOME, true);
  };

  /**
   * Reset welcome message state
   */
  const resetWelcomeMessage = () => {
    SafeStorage.removeItem(STORAGE_KEYS.CHAT_DISMISSED_WELCOME);
  };

  /**
   * Get chat session data
   */
  const getChatSession = () => {
    return {
      hasInteracted: hasInteracted.value,
      preferences: userPreferences.value,
      userInfo: userInfo.value,
      welcomeDismissed: isWelcomeDismissed()
    };
  };

  /**
   * Clear all chat-related data
   */
  const clearAllChatData = () => {
    hasInteracted.value = false;
    userPreferences.value = {
      showWelcomeMessage: true,
      autoShowDelay: 15000,
      soundEnabled: true,
      notificationsEnabled: true
    };
    userInfo.value = null;
    
    SafeStorage.clear();
  };

  /**
   * Export chat data for backup/migration
   */
  const exportChatData = () => {
    return {
      version: '1.0',
      timestamp: new Date().toISOString(),
      data: getChatSession()
    };
  };

  /**
   * Import chat data from backup
   */
  const importChatData = (exportedData) => {
    try {
      if (!exportedData || !exportedData.data) {
        throw new Error('Invalid export data format');
      }

      const { data } = exportedData;
      
      if (data.hasInteracted !== undefined) {
        hasInteracted.value = Boolean(data.hasInteracted);
        SafeStorage.setItem(STORAGE_KEYS.CHAT_INTERACTION, hasInteracted.value);
      }

      if (data.preferences) {
        updatePreferences(data.preferences);
      }

      if (data.userInfo) {
        saveUserInfo(data.userInfo);
      }

      if (data.welcomeDismissed) {
        dismissWelcomeMessage();
      }

      return true;
    } catch (error) {
      console.error('Failed to import chat data:', error);
      return false;
    }
  };

  // Watch for changes and persist to localStorage
  watch(hasInteracted, (newValue) => {
    SafeStorage.setItem(STORAGE_KEYS.CHAT_INTERACTION, newValue);
  });

  watch(userPreferences, (newValue) => {
    SafeStorage.setItem(STORAGE_KEYS.CHAT_PREFERENCES, newValue);
  }, { deep: true });

  watch(userInfo, (newValue) => {
    if (newValue) {
      SafeStorage.setItem(STORAGE_KEYS.CHAT_USER_INFO, newValue);
    } else {
      SafeStorage.removeItem(STORAGE_KEYS.CHAT_USER_INFO);
    }
  });

  // Initialize on first use
  initializeStorage();

  return {
    // Reactive state
    hasInteracted,
    userPreferences,
    userInfo,
    
    // Methods
    markAsInteracted,
    resetInteraction,
    updatePreferences,
    getPreference,
    setPreference,
    saveUserInfo,
    clearUserInfo,
    isWelcomeDismissed,
    dismissWelcomeMessage,
    resetWelcomeMessage,
    getChatSession,
    clearAllChatData,
    exportChatData,
    importChatData,
    
    // Storage utilities
    isStorageAvailable: SafeStorage.isAvailable(),
    
    // Constants
    STORAGE_KEYS
  };
} 